import { type ReactNode, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Save,
  RotateCcw,
  Plus,
  Trash2,
  Pencil,
  Upload,
  Download,
  User,
} from 'lucide-react';
import { DataTableDeleteConfirmModal } from '@shared/components/ui/DataTableDeleteConfirmModal';
import { TableViewMenu, type ViewColumn } from '@shared/components/ui/TableViewMenu';

export interface DataTableToolbarProps {
  newLabel?: string;
  newHref?: string;
  newOnClick?: () => void;
  onSave?: () => void;
  formId?: string;
  onDiscard?: () => void;
  onEdit?: (id: string) => void;
  onEditClick?: () => void;
  selectedIds?: string[];
  onDelete?: (ids: string[]) => void;
  onExport?: () => void;
  exportLabel?: string;
  onImport?: () => void;
  onManageRights?: () => void;
  editMode?: boolean;
  hasTable?: boolean;
  /** Wrap primary action labels in [brackets]. Default false (§2.13). */
  bracketLabels?: boolean;
  /**
   * When true (default), list toolbars only show the primary New action here.
   * Delete / Edit / Export / Import / Manage Rights belong in the bulk bar and Operations menu.
   */
  minimalListChrome?: boolean;
  toolbarEnd?: ReactNode;
  /** Plural noun for delete confirmation, e.g. "candidates". Shown when `onDelete` is set. */
  deleteEntityLabel?: string;
  deleteConfirmBodyExtra?: string;
  /** Passed through to DataTableDeleteConfirmModal — see its requireTypedConfirm prop. */
  deleteRequireTypedConfirm?: string;
  /** When false, hides Import (list views without file import). Default true. */
  showImport?: boolean;
  /** When false, hides Manage Rights. Default true. */
  showManageRights?: boolean;
  /**
   * §28.C — Column visibility / view menu.
   * Pass `tableId` + `viewColumns` + `onViewColumnsChange` to enable.
   * The [View] button sits between Attributes and Import People.
   */
  tableId?: string;
  viewColumns?: ViewColumn[];
  onViewColumnsChange?: (columns: ViewColumn[]) => void;
}

/* §9-A: outline style, h-8, 13px — matches Wrangle toolbar strip */
const btnBase =
  'inline-flex items-center gap-1.5 h-8 px-3 text-[13px] font-medium rounded-[10px] border transition-colors cursor-pointer';
const btnNeutral =
  'border-[var(--vscode-border-primary)] vscode-text-secondary hover:vscode-text-primary hover:bg-[var(--vscode-bg-hover)]';
const btnDisabled = 'border-[var(--vscode-border-primary)] vscode-text-tertiary cursor-not-allowed opacity-40 pointer-events-none';

export function DataTableToolbar({
  newLabel,
  newHref,
  newOnClick,
  onSave,
  onDiscard,
  onEdit,
  onEditClick,
  selectedIds = [],
  onDelete,
  onExport,
  exportLabel = 'Export',
  onImport,
  onManageRights,
  editMode = false,
  formId,
  hasTable = false,
  bracketLabels = false,
  minimalListChrome = true,
  toolbarEnd,
  deleteEntityLabel = 'rows',
  deleteConfirmBodyExtra,
  deleteRequireTypedConfirm,
  showImport = true,
  showManageRights = true,
  tableId,
  viewColumns,
  onViewColumnsChange,
}: DataTableToolbarProps) {
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const b = (s: string) => (bracketLabels ? `[${s}]` : s);
  const defaultManageRights = () => navigate('/team');
  const canEditRow = selectedIds.length === 1 && onEdit;
  const canEditForm = onEditClick && !editMode;
  const firstSelectedId = selectedIds[0];
  const hasRowsSelectedForDelete = selectedIds.length > 0;
  const handleSave = formId
    ? () => document.getElementById(formId)?.requestSubmit()
    : onSave;
  const showSave = editMode && (handleSave || formId);
  const showDiscard = editMode && onDiscard;
  const showNew = hasTable && newLabel && (newHref || newOnClick);
  /** Show Delete on every data-table toolbar; lists without `onDelete` get a click-time message (same idea as Export). */
  const showDelete = hasTable && !editMode && !minimalListChrome;
  const showTableActions = hasTable && !editMode && !minimalListChrome;
  const showRowEditButton = hasTable && !editMode && !minimalListChrome;
  const newInner = (newLabel ?? '').replace(/^\[|\]$/g, '').trim();
  const exportInner = (exportLabel ?? 'Export').replace(/^\[|\]$/g, '').trim() || 'Export';

  return (
    <div className="ggj-data-toolbar flex items-center gap-3 px-4 py-2.5 flex-wrap">
      <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
        {showSave && (
          <button
            type={formId ? 'submit' : 'button'}
            form={formId}
            onClick={!formId ? handleSave : undefined}
            className={`${btnBase} ${btnNeutral}`}
          >
            <Save className="gg-toolbar-ic gg-toolbar-ic--violet" /> {b('Save')}
          </button>
        )}

        {showDiscard && (
          <button type="button" onClick={onDiscard} className={`${btnBase} ${btnNeutral}`}>
            <RotateCcw className="h-3.5 w-3.5 shrink-0 opacity-80" /> {b('Discard')}
          </button>
        )}

        {showNew && newOnClick && (
          <button type="button" onClick={newOnClick} className="inline-flex items-center gap-1.5 h-8 px-3 text-[13px] font-medium rounded-[10px] bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">
            <Plus className="h-3.5 w-3.5 shrink-0" />{' '}
            {bracketLabels ? b(newInner || 'New') : newLabel}
          </button>
        )}
        {showNew && !newOnClick && newHref && (
          <Link to={newHref} className="inline-flex items-center gap-1.5 h-8 px-3 text-[13px] font-medium rounded-[10px] bg-emerald-600 text-white hover:bg-emerald-700 transition-colors">
            <Plus className="h-3.5 w-3.5 shrink-0" />{' '}
            {bracketLabels ? b(newInner || 'New') : newLabel}
          </Link>
        )}

        {showDelete && (
          <button
            type="button"
            disabled={!hasRowsSelectedForDelete || onDelete == null}
            onClick={() => setDeleteOpen(true)}
            className={`${btnBase} ${!hasRowsSelectedForDelete || onDelete == null ? btnDisabled : btnNeutral}`}
            title={
              !hasRowsSelectedForDelete
                ? 'Select one or more rows to delete'
                : onDelete == null
                ? 'Delete not configured for this list'
                : 'Delete selected rows'
            }
          >
            <Trash2 className="h-3.5 w-3.5 shrink-0 opacity-80" /> {b('Delete')}
          </button>
        )}

        {showRowEditButton ? (
          <button
            type="button"
            disabled={!canEditRow && !canEditForm}
            onClick={() => {
              if (canEditRow && firstSelectedId) onEdit?.(firstSelectedId);
              else if (canEditForm) onEditClick?.();
            }}
            className={`${btnBase} ${!canEditRow && !canEditForm ? btnDisabled : btnNeutral}`}
            title={hasTable ? 'Edit selected row' : 'Edit'}
          >
            <Pencil className="h-3.5 w-3.5 shrink-0 opacity-80" /> {b('Edit')}
          </button>
        ) : null}

        {showTableActions && (
          <>
            <button
              type="button"
              disabled={!onExport}
              onClick={onExport ?? undefined}
              className={`${btnBase} ${!onExport ? btnDisabled : btnNeutral}`}
              title={onExport ? 'Export CSV' : 'Export not configured for this list'}
            >
              <Download className="h-3.5 w-3.5 shrink-0 opacity-80" />{' '}
              {bracketLabels ? b(exportInner) : exportLabel}
            </button>
            {/* §28.C View menu — sits between export and import */}
            {tableId && viewColumns && onViewColumnsChange ? (
              <TableViewMenu
                tableId={tableId}
                columns={viewColumns}
                onChange={onViewColumnsChange}
              />
            ) : null}
            {showImport && (
              <button
                type="button"
                disabled={!onImport}
                onClick={onImport ?? undefined}
                className={`${btnBase} ${!onImport ? btnDisabled : btnNeutral}`}
                title={onImport ? 'Import CSV or Excel' : 'Import not configured for this list'}
              >
                <Upload className="h-3.5 w-3.5 shrink-0 opacity-80" /> {b('Import')}
              </button>
            )}
            {showManageRights && (
              <button
                type="button"
                onClick={onManageRights ?? defaultManageRights}
                className={`${btnBase} ${btnNeutral}`}
                title="Open team & access (roles)"
              >
                <User className="h-3.5 w-3.5 shrink-0 opacity-80" /> {b('Manage Rights')}
              </button>
            )}
          </>
        )}
      </div>
      {toolbarEnd ? (
        <div className="flex items-center gap-2 shrink-0 ml-auto justify-end pl-2">
          {toolbarEnd}
        </div>
      ) : null}

      {onDelete != null && (
        <DataTableDeleteConfirmModal
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          entityLabel={deleteEntityLabel}
          count={selectedIds.length}
          bodyExtra={deleteConfirmBodyExtra}
          onConfirm={() => onDelete(selectedIds)}
          requireTypedConfirm={deleteRequireTypedConfirm}
        />
      )}
    </div>
  );
}
